import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import { PCountry } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';

export const moduleConfig = {
  module: {
    title: 'Countries',
    description: 'Manage countries',
    i18nKey: 'p_country',
    permissionModuleKey: 'p_country',
    route: {
      list: '/p_country',
      new: '/p_country/new',
      detail: (id: number) => `/p_country/${id}`,
      edit: (id: number) => `/p_country/${id}/edit`,
    },
    api: {
      list: '/p-country',
      detail: (id: number) => `/p-country/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search countries...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['pc_descr'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'pc_descr',
        label: 'Description',
        placeholder: 'Type to filter description...',
        type: 'text' as const,
        getValue: (item: PCountry) => item.pc_descr,
        options: [],
      },
    ],
    applyFilter: (items: PCountry[], filters: { pc_descr: string[] | string }) => {
      return items.filter((item) => {
        const descrFilter = filters.pc_descr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.pc_descr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.pc_descr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Countrie',
        showTitle: false,
        variant: 'primary' as const,
        href: '/p_country/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: PCountry) => {
      window.location.href = `/p_country/${item.id}`;
    },
    keyExtractor: (item: PCountry) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new countrie.',
    noSearchResults: 'No countries found matching your search criteria.',
    noFilterResults: 'No countries match the selected filters.',
  },
  delete: {
    title: 'Delete Countrie',
    message: 'Are you sure you want to delete this countrie? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete countrie. Please try again.',
    handler: async (id: number) => {
      const { deleteCountry } = await import('../../../services/mutations');
      await deleteCountry(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Countrie Details',
    notFoundTitle: 'Countrie not found',
    notFoundMessage: 'The countrie you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Countries',
    defaultTitle: 'Unnamed Countrie',
    getTitle: (item: PCountry) => {
      return item.pc_descr || `Countrie #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Countrie',
      subtitle: 'Add a new countrie',
      submitText: 'Create Countrie',
    },
    edit: {
      title: 'Edit Countrie',
      subtitle: 'Update countrie information',
      submitText: 'Update Countrie',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading countrie...',
    errorMessage: 'Failed to save countrie. Please try again.',
    create: async (data: Partial<PCountry>) => {
      const { createCountry } = await import('../../../services/mutations');
      return await createCountry(data);
    },
    update: async (id: number, data: Partial<PCountry>) => {
      const { updateCountry } = await import('../../../services/mutations');
      return await updateCountry(id, data);
    },
  },
  fields: [
    {
      name: 'pc_descr',
      label: 'Description',
      type: 'text',
      required: true,
      placeholder: 'Enter countrie description',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Countrie Information',
        icon: '📝',
        fields: ['pc_descr'],
        columns: 1,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<PCountry>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'pc_descr',
    header: 'Description',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
];

export const getDetailSections = (
  item: PCountry,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: [
        {
          label: 'Description',
          value: item.pc_descr || '-',
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: PCountry) => {
  return null;
};

export const getInitialFormData = (): Partial<PCountry> => {
  return {
    pc_descr: '',
  };
};

export const mapItemToFormData = (item: PCountry): Partial<PCountry> => {
  return {
    pc_descr: item.pc_descr || '',
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: PCountry,
  onDelete: (id: number) => void,
  _a?: unknown,
  _b?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <div className="flex items-center space-x-2 flex-nowrap">
      <Link 
        to={moduleConfig.module.route.detail(item.id)}
        className="p-2 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="View"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      </Link>
      {canEdit && (
        <Link 
          to={moduleConfig.module.route.edit(item.id)}
          className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
          onClick={(e) => e.stopPropagation()}
          title="Edit"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </Link>
      )}
      {canDelete && (
        <button 
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item.id);
          }}
          className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
          title="Delete"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
    </div>
  );
};
